import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import useIsMobile from "../hooks/useIsMobile";
import { useAuth } from "../contexts/AuthContext";
import {
  ACTIONS, ACTION_ORDER, HISTORY_LABELS, PRESENCE_LABELS, ROLE_LABELS, formatDateTime, formatPeriod, missionErrorMessage, statusLabel,
} from "../lib/missions";
import { MissionStatusBadge, levelLabel } from "../components/missions/MissionBits";
import { AddMemberDialog, LeadDialog, MissionActionDialog, MissionEditDialog, RemoveMemberDialog } from "../components/missions/MissionDialogs";
import MissionProceduresCard from "../components/procedures/MissionProceduresCard";
import MissionReportCard from "../components/missions/MissionReportCard";
import MissionTimeCard from "../components/attendance/MissionTimeCard";
import MissionClaimsCard from "../components/finance/MissionClaimsCard";
import MissionEquipmentCard from "../components/equipment/MissionEquipmentCard";
import RelatedSessions from "../components/training/RelatedSessions";
import PharmacyTickets from "../components/tickets/PharmacyTickets";
import MissionTicketOrigin from "../components/tickets/MissionTicketOrigin";
import { CATALOG_READ_KEYS, TRAINING_MANAGE_ALL } from "../lib/training";
import { hasPermission } from "../lib/permissions";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuSeparator as DropdownMenuSeparator, ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../components/ui/responsive-dropdown-menu";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowLeft, Building2, ChevronDown, ChevronRight, Crown, IdCard, Mail, MapPin, MoreHorizontal, Pencil, Phone, Plus, RefreshCw, User, UserMinus,
} from "lucide-react";


export default function MissionDetail() {
  const { id } = useParams();
  const [mission, setMission] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | ready | missing | error
  const [errorText, setErrorText] = useState("");
  const [history, setHistory] = useState(null);
  const [action, setAction] = useState(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [target, setTarget] = useState(null); // assignment for remove / lead
  const [removeOpen, setRemoveOpen] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [procedures, setProcedures] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const { user } = useAuth();

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/missions/${id}/history`);
      setHistory(data.items);
    } catch (e) {
      setHistory("error");
    }
  }, [id]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setPhase("loading");
    try {
      const { data } = await api.get(`/missions/${id}`);
      setMission(data);
      setPhase("ready");
      loadHistory();
    } catch (e) {
      if (e?.response?.status === 404) { setPhase("missing"); return; }
      setErrorText(missionErrorMessage(e, "Impossible de charger la mission."));
      setPhase("error");
    }
  }, [id, loadHistory]);
  useEffect(() => { load(); }, [load]);

  // Every write answers with the updated mission: the page shows it and re-reads the history.
  const done = (data, success) => {
    setMission(data);
    setActionOpen(false); setEditOpen(false); setAddOpen(false); setRemoveOpen(false); setLeadOpen(false);
    toast.success(success);
    loadHistory();
  };
  const conflict = () => load({ silent: true });
  const openAction = (a) => { setAction(a); setActionOpen(true); };

  if (phase === "loading") return <div className="space-y-3" data-testid="mission-loading"><Skeleton className="h-8 w-72" /><Skeleton className="h-40 w-full" /><Skeleton className="h-40 w-full" /></div>;
  if (phase === "missing" || phase === "error") {
    return (
      <Card className="p-8 text-center space-y-3 max-w-xl mx-auto" data-testid={phase === "missing" ? "mission-missing" : "mission-error"}>
        <AlertTriangle className="mx-auto text-slate-300" />
        <p className="text-sm text-slate-700">{phase === "missing" ? "Cette mission n'existe pas ou vous n'y avez pas accès." : errorText}</p>
        <div className="flex justify-center gap-2">
          <Button variant="outline" asChild><Link to="/missions">Retour aux missions</Link></Button>
          {phase === "error" && <Button variant="outline" onClick={() => load()}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>}
        </div>
      </Card>
    );
  }

  const caps = mission.capabilities;
  const available = ACTION_ORDER.filter((a) => caps[a]);
  const primary = available.filter((a) => ACTIONS[a].primary);
  const secondary = available.filter((a) => !ACTIONS[a].primary);
  const hasMenu = secondary.length > 0 || caps.edit || caps.delete;
  const confirmDelete = async (reason) => {
    try {
      await api.delete(`/missions/${id}`, { data: { reason } });
      toast.success("Mission mise à la corbeille");
      navigate("/missions");
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de supprimer la mission."));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="mission-page">
      <Link to="/missions" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ArrowLeft size={14} /> Retour aux missions</Link>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-slate-500" data-testid="mission-reference">{mission.reference}</span>
            <MissionStatusBadge status={mission.status} />
          </div>
          <div className="flex items-start gap-2">
            <h1 className="font-display font-bold text-2xl text-slate-900 break-words min-w-0 flex-1" data-testid="mission-title">{mission.title}</h1>
            {hasMenu && mobile && <div><ActionsMenu caps={caps} secondary={secondary} onAction={openAction} onEdit={() => setEditOpen(true)} onDelete={() => setDeleting(mission)} /></div>}
          </div>
          <p className="text-sm text-slate-500">{subtitle(mission)}</p>
          <MissionTicketOrigin missionId={mission.id} />
          {mission.status_reason && ["postponed", "cancelled", "interrupted"].includes(mission.status) && (
            <p className="text-sm text-slate-600" data-testid="mission-status-reason">Motif : {mission.status_reason}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {primary.map((a) => (
            <Button key={a} onClick={() => openAction(a)} className={`flex-1 md:flex-none`} data-testid={`mission-action-${a}`}>{ACTIONS[a].label}</Button>
          ))}
          {hasMenu && !mobile && <div><ActionsMenu caps={caps} secondary={secondary} onAction={openAction} onEdit={() => setEditOpen(true)} onDelete={() => setDeleting(mission)} /></div>}
        </div>
      </div>

      {mission.status === "draft" && caps.plan && !mission.lead && (
        <Card className="p-4 border-amber-200 bg-amber-50 text-sm text-amber-900" data-testid="mission-lead-needed">
          Désignez un responsable dans l'équipe pour pouvoir planifier la mission.
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem] items-start">
        <div className="space-y-4 min-w-0">
          <MissionReportCard mission={mission} />
          <TeamCard mission={mission} onAdd={() => setAddOpen(true)}
            onRemove={(a) => { setTarget(a); setRemoveOpen(true); }} onLead={(a) => { setTarget(a); setLeadOpen(true); }} />
          {user?.features?.attendance && <MissionTimeCard mission={mission} userId={user.id} />}
          {user?.features?.finance && (hasPermission(user, "finance:submit_claims") || hasPermission(user, "finance:approve_claims")) && (
            <MissionClaimsCard mission={mission} canClaim={hasPermission(user, "finance:submit_claims")} canApprove={hasPermission(user, "finance:approve_claims")} />
          )}
          {user?.features?.procedures && <MissionProceduresCard mission={mission} onLoaded={setProcedures} />}
          {user?.features?.equipment && <MissionEquipmentCard mission={mission} userId={user.id} />}
          <PharmacyTickets prospectId={mission.prospect_id} linkedTo={{ mission_id: mission.id }} />
          {user?.features?.training && hasPermission(user, CATALOG_READ_KEYS) && (
            <RelatedSessions params={{ mission_id: mission.id }} testid="mission-sessions" emptyText="Aucune session de formation pour cette mission."
              createLink={(caps.edit || (hasPermission(user, TRAINING_MANAGE_ALL) && !["completed", "cancelled", "interrupted"].includes(mission.status)))
                ? `/formations/sessions/nouvelle?mission=${mission.id}` : null} />
          )}
          <ProgramCard mission={mission} />
          <HistoryCard history={history} onRetry={loadHistory} />
        </div>
        <PharmacyCard mission={mission} />
      </div>

      <MissionActionDialog mission={mission} action={action} open={actionOpen} onOpenChange={setActionOpen} onDone={done} onConflict={conflict}
        missingProcedures={procedures?.missing_required || []} />
      <DeleteWithReasonDialog
        target={deleting} testId="delete-mission" onClose={() => setDeleting(null)} onConfirm={confirmDelete}
        title={`Supprimer la mission ${deleting?.reference || ""} ?`}
        description="Elle n'a pas démarré et n'a laissé aucune trace d'exécution. Elle va dans la corbeille : vous pourrez la restaurer pendant 30 jours."
      />
      <MissionEditDialog mission={mission} open={editOpen} onOpenChange={setEditOpen} onDone={done} onConflict={conflict} />
      <AddMemberDialog mission={mission} open={addOpen} onOpenChange={setAddOpen} onDone={done} onConflict={conflict} />
      <RemoveMemberDialog mission={mission} assignment={target} open={removeOpen} onOpenChange={setRemoveOpen} onDone={done} onConflict={conflict} />
      <LeadDialog mission={mission} assignment={target} open={leadOpen} onOpenChange={setLeadOpen} onDone={done} onConflict={conflict} />
    </div>
  );
}

// Period first; the type and the pharmacy only when the title does not already say them (« Installation — Pharmacie X »).
function subtitle(mission) {
  const title = mission.title.toLowerCase();
  return [
    formatPeriod(mission.planned_start_at, mission.planned_end_at, mission.timezone),
    title.includes(mission.type.label.toLowerCase()) ? null : mission.type.label,
    title.includes(mission.prospect.pharmacy_name.toLowerCase()) ? null : mission.prospect.pharmacy_name,
  ].filter(Boolean).join(" · ");
}

function ActionsMenu({ caps, secondary, onAction, onEdit, onDelete }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Autres actions" data-testid="mission-more"><MoreHorizontal size={16} /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" title="Actions sur la mission">
        {caps.edit && <DropdownMenuItem onClick={onEdit} data-testid="mission-edit"><Pencil size={14} className="mr-2" />Modifier</DropdownMenuItem>}
        {caps.edit && secondary.length > 0 && <DropdownMenuSeparator />}
        {secondary.map((a) => (
          <DropdownMenuItem key={a} onClick={() => onAction(a)} className={ACTIONS[a].danger ? "text-red-700" : ""} data-testid={`mission-action-${a}`}>{ACTIONS[a].label}</DropdownMenuItem>
        ))}
        {caps.delete && <DropdownMenuItem onClick={onDelete} className="text-red-700" data-testid="mission-delete">Supprimer…</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MemberRow({ assignment, canManage, onRemove, onLead }) {
  const { member } = assignment;
  const isLead = assignment.role_in_mission === "lead";
  return (
    <div className="flex items-center gap-3 py-3" data-testid={`mission-member-${member.kind}-${member.id}`}>
      <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0 text-slate-500">
        {member.kind === "user" ? <User size={15} /> : <IdCard size={15} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-900 break-words">{member.name}</span>
          {isLead && <span className="inline-flex items-center gap-1 rounded-full bg-jamora-50 px-2 py-0.5 text-xs font-medium text-jamora"><Crown size={11} />Responsable</span>}
        </div>
        <div className="text-xs text-slate-500">
          {!isLead && `${ROLE_LABELS[assignment.role_in_mission]} · `}{PRESENCE_LABELS[assignment.expected_presence]}
          {member.kind === "collaborator" && " · sans compte CRM"}
          {assignment.warnings_confirmed.length > 0 && <span className="text-amber-700"> · avertissement confirmé</span>}
        </div>
      </div>
      {canManage && !isLead && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={`Actions pour ${member.name}`} data-testid={`mission-member-menu-${member.id}`}><MoreHorizontal size={15} /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" title={member.name}>
            <DropdownMenuItem onClick={onLead} data-testid={`mission-member-lead-${member.id}`}><Crown size={14} className="mr-2" />Désigner responsable</DropdownMenuItem>
            <DropdownMenuItem onClick={onRemove} className="text-red-700" data-testid={`mission-member-remove-${member.id}`}><UserMinus size={14} className="mr-2" />Retirer de l'équipe</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function TeamCard({ mission, onAdd, onRemove, onLead }) {
  const [showRemoved, setShowRemoved] = useState(false);
  const canManage = mission.capabilities.manage_team;
  const team = [...mission.team].sort((a, b) => (a.role_in_mission === "lead" ? -1 : b.role_in_mission === "lead" ? 1 : 0));
  return (
    <Card className="p-5" data-testid="mission-team">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-slate-900">Équipe</h3>
        {canManage && <Button variant="outline" size="sm" onClick={onAdd} data-testid="mission-add-member"><Plus size={14} className="mr-1.5" />Ajouter</Button>}
      </div>
      {team.length === 0 ? (
        <p className="text-sm text-slate-500 py-3">Personne n'est encore affecté à cette mission.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {team.map((a) => <MemberRow key={a.id} assignment={a} canManage={canManage} onRemove={() => onRemove(a)} onLead={() => onLead(a)} />)}
        </div>
      )}
      {mission.removed.length > 0 && (
        <div className="pt-2 border-t border-slate-100">
          <button type="button" className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 py-1" onClick={() => setShowRemoved((v) => !v)}
            aria-expanded={showRemoved} data-testid="mission-removed-toggle">
            {showRemoved ? <ChevronDown size={13} /> : <ChevronRight size={13} />}{mission.removed.length} personne{mission.removed.length > 1 ? "s" : ""} retirée{mission.removed.length > 1 ? "s" : ""}
          </button>
          {showRemoved && (
            <ul className="space-y-1.5 pt-1" data-testid="mission-removed-list">
              {mission.removed.map((a) => (
                <li key={a.id} className="text-xs text-slate-500">
                  <span className="text-slate-700">{a.member.name}</span>, retiré le {formatDateTime(a.removed_at, mission.timezone)} par {a.removed_by_name}{a.removed_reason ? ` : « ${a.removed_reason} »` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

function ProgramCard({ mission }) {
  const rows = [
    ["Type", mission.type.label],
    ...(mission.pilot_id ? [["Pilote", mission.prospect.can_open
      ? <Link to={`/pilotes/${mission.prospect.id}`} className="text-jamora hover:underline" data-testid="mission-open-pilot">Rattachée au pilote de la pharmacie</Link>
      : "Rattachée au pilote de la pharmacie"]] : []),
    ["Début prévu", formatDateTime(mission.planned_start_at, mission.timezone)],
    ["Fin prévue", formatDateTime(mission.planned_end_at, mission.timezone)],
    ...(mission.started_at ? [["Démarrée le", formatDateTime(mission.started_at, mission.timezone)]] : []),
    ...(mission.ended_at ? [[mission.status === "interrupted" ? "Interrompue le" : "Terminée le", formatDateTime(mission.ended_at, mission.timezone)]] : []),
    ["Créée par", `${mission.created_by_name}, le ${formatDateTime(mission.created_at, mission.timezone)}`],
  ];
  const skills = mission.type.required_skills || [];
  return (
    <Card className="p-5 space-y-4" data-testid="mission-program">
      <h3 className="text-base font-semibold text-slate-900">Programme</h3>
      <div>
        <div className="text-xs font-medium text-slate-500 mb-1">Objectif</div>
        <p className="text-sm text-slate-800 whitespace-pre-line break-words">{mission.objective || <span className="text-slate-400">Non précisé</span>}</p>
      </div>
      <dl className="grid gap-x-4 gap-y-0.5 sm:gap-y-2 sm:grid-cols-[10rem_minmax(0,1fr)] text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-slate-500">{label}</dt>
            <dd className="text-slate-800 break-words mb-2 sm:mb-0">{value}</dd>
          </div>
        ))}
      </dl>
      {skills.length > 0 && (
        <div>
          <div className="text-xs font-medium text-slate-500 mb-1">Compétences attendues</div>
          <p className="text-sm text-slate-700">{skills.map((s) => `${s.label} (${levelLabel(s.min_level)})`).join(" · ")}</p>
        </div>
      )}
    </Card>
  );
}

function PharmacyCard({ mission }) {
  const c = mission.contact;
  const place = c ? [c.address, c.commune, c.region].filter(Boolean).join(", ") : "";
  return (
    <Card className="p-5 space-y-3 lg:sticky lg:top-24" data-testid="mission-pharmacy">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Building2 size={16} className="text-slate-400" />Pharmacie</h3>
        {mission.prospect.can_open && (
          <Link to={`/prospects/${mission.prospect.id}`} className="text-xs text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline" data-testid="mission-open-prospect">Ouvrir la fiche</Link>
        )}
      </div>
      <p className="font-medium text-slate-900 break-words">{mission.prospect.pharmacy_name}</p>
      {!c ? <p className="text-sm text-slate-500">Coordonnées indisponibles.</p> : (
        <ul className="space-y-2 text-sm text-slate-700">
          {place && <li className="flex gap-2"><MapPin size={15} className="text-slate-400 shrink-0 mt-0.5" /><span className="break-words">{place}</span></li>}
          {c.phone && <li className="flex gap-2"><Phone size={15} className="text-slate-400 shrink-0 mt-0.5" /><a href={`tel:${c.phone}`} className="hover:underline">{c.phone}</a></li>}
          {c.email && <li className="flex gap-2"><Mail size={15} className="text-slate-400 shrink-0 mt-0.5" /><a href={`mailto:${c.email}`} className="hover:underline break-all">{c.email}</a></li>}
          {c.operational_contact?.name && (
            <li className="flex gap-2"><User size={15} className="text-slate-400 shrink-0 mt-0.5" />
              <span>{c.operational_contact.name}{c.operational_contact.function ? ` · ${c.operational_contact.function}` : ""}</span></li>
          )}
          {!place && !c.phone && !c.email && !c.operational_contact?.name && <li className="text-slate-500">Aucune coordonnée renseignée.</li>}
        </ul>
      )}
    </Card>
  );
}

function historyText(item) {
  const label = HISTORY_LABELS[item.kind] || item.kind;
  const who = item.data?.member?.name || item.data?.lead?.name;
  return who ? `${label} : ${who}` : label;
}

function HistoryCard({ history, onRetry }) {
  return (
    <Card className="p-5 space-y-3" data-testid="mission-history">
      <h3 className="text-base font-semibold text-slate-900">Historique</h3>
      {history === null ? <Skeleton className="h-16 w-full" /> : history === "error" ? (
        <div className="text-sm text-slate-600 flex items-center gap-2">Impossible de charger l'historique.<Button variant="outline" size="sm" onClick={onRetry}>Réessayer</Button></div>
      ) : history.length === 0 ? <p className="text-sm text-slate-500">Rien pour le moment.</p> : (
        <ol className="space-y-3">
          {history.map((h) => (
            <li key={h.id} className="text-sm" data-testid="mission-history-item">
              <div className="text-slate-800">{historyText(h)}{h.data?.to ? <span className="text-slate-500"> ({statusLabel(h.data.to)})</span> : null}</div>
              <div className="text-xs text-slate-500">{formatDateTime(h.at)} · {h.actor_name}{h.data?.warnings?.length ? " · avertissement confirmé" : ""}</div>
              {h.reason && <div className="text-xs text-slate-600 mt-0.5">Motif : {h.reason}</div>}
              {h.data?.derogation && (
                <div className="text-xs text-amber-800 mt-0.5" data-testid="mission-history-derogation">
                  Dérogation ({h.data.derogation.procedures.join(", ")} non terminée{h.data.derogation.procedures.length > 1 ? "s" : ""}) : {h.data.derogation.reason}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
